import React, { useEffect, useRef, useState } from "react";
import {
  Users, Volume2, VolumeX, Maximize, Cast, RotateCw, Play,
  MessageSquare, ChevronRight, Send, Heart, Sparkles,
} from "lucide-react";
import { fetchIceServers, makeWsUrl, newPeerConnection } from "../lib/webrtc";
import { useAuth } from "../context/AuthContext";
import { useEmoteBursts, EmotePicker } from "./Emotes";
import NewsroomOverlayRender from "./NewsroomOverlayRender";
import api from "../lib/api";

/**
 * WebRTC viewer — YouTube / Netflix-style layout.
 *
 * Layout:
 *   [ Video player (big, focal)                      ] [ Chat drawer ]
 *   [ Control bar below video: mute · AirPlay · rotate · fullscreen · emote picker · chat toggle ]
 *
 * Chat is **collapsible**: a right-side drawer on desktop that can be hidden
 * with a chevron button. On mobile it collapses into a sheet.
 *
 * Brand colors via CSS vars (--tv-accent yellow, --tv-brand cyan, --tv-live red,
 * --tv-bg near-black) — no hard-coded purples/grays.
 */
export default function StreamViewer({ streamId, kidsBrand = false }) {
  const { user, token } = useAuth();
  const [connected, setConnected] = useState(false);
  const [muted, setMuted] = useState(true);
  const [needsUserGesture, setNeedsUserGesture] = useState(false);
  const [viewersCount, setViewersCount] = useState(0);
  const [chat, setChat] = useState([]);
  const [chatMsg, setChatMsg] = useState("");
  const [chatOpen, setChatOpen] = useState(false);
  const [overlays, setOverlays] = useState({});
  const [guestCams, setGuestCams] = useState({ cam1: null, cam2: null, cam3: null });
  const [rotation, setRotation] = useState(0);
  const [castSupported, setCastSupported] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [iceReconnecting, setIceReconnecting] = useState(false);
  // After ~25s of WAITING FOR HOST with no broadcaster_id, surface a clearer
  // explanation + retry button. Avoids the "stuck on Waiting" black-screen
  // panic when the host hasn't actually started broadcasting yet (e.g. they
  // hit "Go Live" but never granted camera permission, or their browser
  // tab crashed). Backend `/api/live/{id}` is the source of truth.
  const [waitingTooLong, setWaitingTooLong] = useState(false);
  const [streamHealth, setStreamHealth] = useState(null); // {is_live, host_online}
  const _broadcasterIdRef = useRef(null);
  // Mirror the ref into state so the LIVE / WAITING FOR HOST badge
  // re-renders when the host comes online AFTER the viewer connected.
  // Refs alone don't trigger re-renders, so without this mirror viewers
  // were stuck on "WAITING FOR HOST" even after the host's host_online
  // event landed and updated the ref. (Declared up-front so the
  // watchdog effect below can reference it without a TDZ error.)
  const [broadcasterIdState, setBroadcasterIdState] = useState(null);
  const iceReconnectTimerRef = useRef(null);
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const wsRef = useRef(null);
  const pcRef = useRef(null);
  const iceServersRef = useRef(null);
  const readyRetryRef = useRef(null);
  const chatScrollRef = useRef(null);
  // --- Resilience (keepalive + WS auto-reconnect) ---
  const keepAliveRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const wantCloseRef = useRef(false);
  const { add: addEmote, Overlay: EmoteOverlay } = useEmoteBursts();

  // --- WebRTC wiring (unchanged from iter23) ---
  useEffect(() => {
    let mounted = true;
    wantCloseRef.current = false;
    reconnectAttemptsRef.current = 0;
    (async () => {
      iceServersRef.current = await fetchIceServers();
      if (!mounted) return;
      openWs();
    })();
    return () => { mounted = false; cleanup(); };
    // eslint-disable-next-line
  }, [streamId]);

  useEffect(() => {
    chatScrollRef.current?.scrollTo({ top: chatScrollRef.current.scrollHeight, behavior: "smooth" });
  }, [chat.length]);

  // ── Waiting-for-host watchdog ───────────────────────────────────────
  // If the viewer hasn't seen a broadcaster_id and isn't connected after
  // 25s, escalate the UX: poll /api/live/{id} every 10s for an
  // authoritative is_live flag and surface an actionable "host hasn't
  // started" panel instead of a frozen "WAITING FOR HOST" badge.
  useEffect(() => {
    if (connected || broadcasterIdState) {
      setWaitingTooLong(false);
      setStreamHealth(null);
      return;
    }
    const t = setTimeout(() => setWaitingTooLong(true), 25000);
    return () => clearTimeout(t);
  }, [connected, broadcasterIdState]);

  useEffect(() => {
    if (!waitingTooLong) return;
    let cancelled = false;
    const ping = async () => {
      try {
        const { data } = await api.get(`/live/${streamId}`);
        if (cancelled) return;
        setStreamHealth({
          is_live: !!data?.is_live,
          status: data?.status,
          provider: data?.provider,
          viewer_count: data?.viewer_count,
        });
      } catch {
        if (!cancelled) setStreamHealth({ is_live: false, status: "unknown" });
      }
    };
    ping();
    const t = setInterval(ping, 10000);
    return () => { cancelled = true; clearInterval(t); };
  }, [waitingTooLong, streamId]);

  // Fetch guest-cam attribution once on load — the WS `guest_cams` frame will
  // keep it fresh after any host-driven change.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/live/${streamId}/guest-cams`);
        if (!cancelled && data) setGuestCams(data);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [streamId]);

  const cleanup = () => {
    wantCloseRef.current = true;
    if (readyRetryRef.current) { clearInterval(readyRetryRef.current); readyRetryRef.current = null; }
    if (keepAliveRef.current) { clearInterval(keepAliveRef.current); keepAliveRef.current = null; }
    if (reconnectTimerRef.current) { clearTimeout(reconnectTimerRef.current); reconnectTimerRef.current = null; }
    if (iceReconnectTimerRef.current) { clearTimeout(iceReconnectTimerRef.current); iceReconnectTimerRef.current = null; }
    if (pcRef.current) { try { pcRef.current.close(); } catch {} pcRef.current = null; }
    if (wsRef.current) { try { wsRef.current.close(); } catch {} wsRef.current = null; }
  };

  const ensurePc = () => {
    if (pcRef.current) return pcRef.current;
    const pc = newPeerConnection(iceServersRef.current);
    pcRef.current = pc;
    pc.ontrack = (e) => {
      if (videoRef.current) {
        const v = videoRef.current;
        const incoming = e.streams[0];
        if (!incoming) return;
        // Dedupe srcObject rebinds: if the SAME stream (or same video
        // track id) is already bound, don't clobber — clobbering pauses
        // the element briefly, and on mobile the browser may refuse to
        // auto-resume (autoplay policy) forcing the user to tap Play.
        // This is THE root cause of the "viewers hit play every ~10s"
        // bug: every ICE flutter triggered a new ontrack → new
        // srcObject → pause → blocked auto-resume.
        const prev = v.srcObject;
        const prevId = prev && prev.getVideoTracks ? prev.getVideoTracks()[0]?.id : null;
        const nextId = incoming.getVideoTracks?.()[0]?.id || null;
        if (prev === incoming || (prevId && nextId && prevId === nextId)) {
          // Same stream already bound — make sure it's playing and bail.
          if (v.paused) v.play().catch(() => setNeedsUserGesture(true));
          return;
        }
        try { v.srcObject = incoming; } catch { /* ignore */ }
        v.play().catch(() => setNeedsUserGesture(true));
      }
    };
    pc.onicecandidate = (e) => {
      if (e.candidate && wsRef.current?.readyState === 1) {
        wsRef.current.send(JSON.stringify({ type: "ice", to: _broadcasterIdRef.current, payload: e.candidate.toJSON() }));
      }
    };
    pc.oniceconnectionstatechange = () => {
      const st = pc.iceConnectionState;
      const healthy = st === "connected" || st === "completed";
      setConnected(healthy);

      // ── Reconnect toast/badge (debounced) ───────────────────────────────
      // Show "Reconnecting…" only if we've been in a non-healthy state for
      // more than 3s — avoids flickering the badge on brief checking blips
      // that every healthy ICE handshake passes through. Clears instantly
      // the moment the state goes healthy again.
      if (healthy) {
        if (iceReconnectTimerRef.current) {
          clearTimeout(iceReconnectTimerRef.current);
          iceReconnectTimerRef.current = null;
        }
        setIceReconnecting(false);
      } else if (st === "failed" || st === "disconnected") {
        if (!iceReconnectTimerRef.current) {
          iceReconnectTimerRef.current = setTimeout(() => {
            setIceReconnecting(true);
            iceReconnectTimerRef.current = null;
          }, 3000);
        }
      }

      // ICE failed/disconnected on the viewer side: poke the host for a fresh
      // offer-with-iceRestart by re-sending viewer_ready. The host's sendOffer
      // will be called again, producing a new SDP with fresh candidates.
      if (st === "failed" || st === "disconnected") {
        try { pc.restartIce && pc.restartIce(); } catch {}
        sendReady();
      }
    };
    return pc;
  };

  const openWs = () => {
    const url = makeWsUrl(`/api/ws/stream/${streamId}?role=viewer&token=${encodeURIComponent(token || "")}`);
    const ws = new WebSocket(url);
    wsRef.current = ws;
    ws.onopen = () => {
      setReconnecting(false);
      reconnectAttemptsRef.current = 0;
      // Keepalive pings so the WS doesn't idle-drop behind proxies
      if (keepAliveRef.current) clearInterval(keepAliveRef.current);
      keepAliveRef.current = setInterval(() => {
        if (ws.readyState === 1) ws.send(JSON.stringify({ type: "ping", payload: { t: Date.now() } }));
      }, 25000);
    };
    ws.onclose = () => {
      try { console.warn("[viewer] WS closed, reconnect scheduled"); } catch {}
      setConnected(false);
      setBroadcasterIdState(null);
      _broadcasterIdRef.current = null;
      if (keepAliveRef.current) { clearInterval(keepAliveRef.current); keepAliveRef.current = null; }
      // IMPORTANT: do NOT close the RTCPeerConnection here. WebRTC
      // media flows independently of the signaling WS once negotiated.
      // Closing the PC on every brief WS blip forces the viewer to
      // re-download the stream via a fresh offer/answer — THAT was
      // the "stream cuts out every 10s and I have to refresh" bug.
      // If the WS genuinely can't reconnect after N tries we'll tear
      // down elsewhere. For now, just reconnect signaling silently.
      // DO NOT close the existing PC on a signaling reconnect — media
      // flows independently of the signaling WS. If welcome/offer come
      // back with a fresh broadcaster_id for a brand-new session we'll
      // rebuild then; in the common "brief WS blip" case, the PC
      // keeps delivering frames and the viewer sees nothing.
      if (wantCloseRef.current) return; // unmount or explicit close, don't reconnect
      // Exponential backoff, capped at 10s
      const attempts = reconnectAttemptsRef.current + 1;
      reconnectAttemptsRef.current = attempts;
      setReconnecting(true);
      const delay = Math.min(10000, 1000 * Math.pow(2, Math.min(attempts - 1, 4)));
      reconnectTimerRef.current = setTimeout(() => {
        if (!wantCloseRef.current) openWs();
      }, delay);
    };
    ws.onmessage = async (evt) => {
      const msg = safeJson(evt.data);
      if (!msg) return;
      if (msg.type === "pong") return; // keepalive reply, no-op
      if (msg.type === "welcome") {
        setViewersCount(msg.payload?.viewers_count || 0);
        setChat(msg.payload?.chat_log || []);
        setOverlays(msg.payload?.overlays || {});
        const newBroadcasterId = msg.payload?.broadcaster_id || null;
        const prevBroadcasterId = _broadcasterIdRef.current;
        // If the broadcaster's peer_id changed (host actually
        // reconnected and got a fresh peer_id), the old PC is dead —
        // drop it so the welcome→offer round-trip rebuilds fresh.
        // If it's the SAME peer_id, we're on a brief WS blip; the PC
        // is still delivering frames, don't touch it.
        if (prevBroadcasterId && newBroadcasterId && prevBroadcasterId !== newBroadcasterId) {
          if (pcRef.current) { try { pcRef.current.close(); } catch {} pcRef.current = null; }
        }
        _broadcasterIdRef.current = newBroadcasterId;
        setBroadcasterIdState(newBroadcasterId);
        // Only re-send ready if we don't already have a healthy PC.
        const stCur = pcRef.current?.iceConnectionState;
        const pcHealthy = stCur === "connected" || stCur === "completed" || stCur === "checking";
        if (!pcHealthy) sendReady();
        // Retry `viewer_ready` ONLY while we haven't received an offer yet.
        // Once an offer arrives we clear this interval below — otherwise we
        // keep pinging the host even after a healthy connection, and the
        // host's handleViewerReady() dutifully tears down the existing PC
        // and starts a new negotiation. That loop is exactly what caused
        // the "stream stops every 10s" bug users hit.
        if (readyRetryRef.current) clearInterval(readyRetryRef.current);
        readyRetryRef.current = setInterval(() => {
          const st = pcRef.current?.iceConnectionState;
          // Any of these states = we already have a PC; don't re-ready.
          if (st === "connected" || st === "completed" || st === "checking" || st === "new") return;
          sendReady();
        }, 5000);
      } else if (msg.type === "host_online") {
        _broadcasterIdRef.current = msg.payload?.host_id || _broadcasterIdRef.current;
        setBroadcasterIdState(_broadcasterIdRef.current);
        sendReady();
      } else if (msg.type === "host_offline" || msg.type === "stream_ended") {
        setConnected(false);
        _broadcasterIdRef.current = null;
        setBroadcasterIdState(null);
        if (pcRef.current) { try { pcRef.current.close(); } catch {} pcRef.current = null; }
      } else if (msg.type === "offer") {
        _broadcasterIdRef.current = msg.from;
        setBroadcasterIdState(msg.from);
        // Once the host sent us an offer, we no longer need to keep
        // pinging with viewer_ready — that retry is only meant to cover
        // the case where the host wasn't in the peer table yet when we
        // first sent it.
        if (readyRetryRef.current) { clearInterval(readyRetryRef.current); readyRetryRef.current = null; }
        const pc = ensurePc();
        try {
          await pc.setRemoteDescription(new RTCSessionDescription(msg.payload));
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          wsRef.current?.send(JSON.stringify({ type: "answer", to: msg.from, payload: { type: answer.type, sdp: answer.sdp } }));
        } catch {}
      } else if (msg.type === "ice") {
        const pc = ensurePc();
        try { await pc.addIceCandidate(msg.payload); } catch {}
      } else if (msg.type === "chat") {
        setChat((prev) => [...prev.slice(-200), msg.payload]);
      } else if (msg.type === "emote") {
        addEmote(msg.payload?.emote || "🎉");
      } else if (msg.type === "overlays") {
        setOverlays(msg.payload || {});
      } else if (msg.type === "viewer_count") {
        setViewersCount(msg.payload?.viewer_count || 0);
      } else if (msg.type === "comments_toggle") {
        setOverlays((o) => ({ ...o, _comments_enabled: msg.payload?.enabled }));
      } else if (msg.type === "guest_cams") {
        setGuestCams(msg.payload || { cam1: null, cam2: null, cam3: null });
      } else if (msg.type === "fx_settings") {
        // Cache the latest Studio FX payload for the stream so guest broadcasters
        // (or any embed that wants to mirror the host's look) can apply per-slot
        // FX. Exposed via window event so other components can subscribe without
        // needing to thread the state through props.
        try {
          window.__tapin_fx_settings = msg.payload || null;
          window.dispatchEvent(new CustomEvent("tapin:fx_settings", { detail: msg.payload }));
        } catch {}
      }
    };
  };

  const sendReady = () => {
    if (wsRef.current?.readyState === 1) wsRef.current.send(JSON.stringify({ type: "viewer_ready" }));
  };

  const sendChat = (e) => {
    e.preventDefault();
    if (!user || !chatMsg.trim()) return;
    wsRef.current?.send(JSON.stringify({ type: "chat", payload: { text: chatMsg.trim() } }));
    setChatMsg("");
  };

  const sendEmote = (emote) => {
    if (!user) return;
    wsRef.current?.send(JSON.stringify({ type: "emote", payload: { emote } }));
  };

  // --- Auto-resume watchdog ---
  // Viewers reported the stream pausing every ~10 seconds and having to
  // tap Play again. Browsers will pause an autoplay <video> on the slightest
  // buffer stall, tab visibility change, or PIP exit. We listen for the
  // pause/waiting/stalled events and call .play() again automatically.
  // If the browser refuses (autoplay policy), we surface the manual Play
  // overlay one time — but most cases are handled silently.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    let pokeTimer = null;
    const tryResume = () => {
      // Don't fight the user if they explicitly paused via a Play button
      // (we can't reliably detect that, so we resume but bail if blocked).
      if (!v.paused) return;
      v.play()
        .then(() => setNeedsUserGesture(false))
        .catch(() => { /* autoplay blocked — surface gesture prompt */ setNeedsUserGesture(true); });
    };
    const onPause = () => { pokeTimer = setTimeout(tryResume, 250); };
    const onWaiting = () => { pokeTimer = setTimeout(tryResume, 500); };
    const onStalled = () => { pokeTimer = setTimeout(tryResume, 500); };
    const onEnded = () => { /* live streams shouldn't end normally; try once */ tryResume(); };
    // When the browser tab / phone screen comes back into focus, auto
    // resume. iOS Safari + Android Chrome pause the element when the
    // page loses visibility or the screen locks — this catches that.
    const onVisibility = () => { if (!document.hidden) tryResume(); };
    const onPageShow = () => tryResume();
    v.addEventListener("pause", onPause);
    v.addEventListener("waiting", onWaiting);
    v.addEventListener("stalled", onStalled);
    v.addEventListener("ended", onEnded);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("focus", onPageShow);
    return () => {
      if (pokeTimer) clearTimeout(pokeTimer);
      v.removeEventListener("pause", onPause);
      v.removeEventListener("waiting", onWaiting);
      v.removeEventListener("stalled", onStalled);
      v.removeEventListener("ended", onEnded);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("focus", onPageShow);
    };
  }, []);

  // --- Video controls ---
  const toggleMute = () => {
    const v = videoRef.current; if (!v) return;
    v.muted = !v.muted; setMuted(v.muted);
    if (!v.muted && v.paused) v.play().catch(() => {});
  };

  const manualPlay = () => {
    const v = videoRef.current; if (!v) return;
    v.muted = false; setMuted(false);
    v.play().then(() => setNeedsUserGesture(false)).catch(() => {});
  };

  const toggleFullscreen = async () => {
    const el = containerRef.current;
    const v = videoRef.current;
    if (!el || !v) return;
    // Already fullscreen? Exit.
    if (document.fullscreenElement || document.webkitFullscreenElement) {
      try {
        if (document.exitFullscreen) await document.exitFullscreen();
        else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      } catch { /* ignore */ }
      return;
    }
    // iOS Safari only supports fullscreen on the <video> element itself
    // (webkitEnterFullscreen), NOT on arbitrary <div> containers. Try
    // that path first on iOS, fall back to the standard element
    // fullscreen API everywhere else (Chrome, Firefox, desktop Safari).
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    if (isIOS && typeof v.webkitEnterFullscreen === "function") {
      try { v.webkitEnterFullscreen(); return; } catch { /* fall through */ }
    }
    try {
      if (el.requestFullscreen) await el.requestFullscreen();
      else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
      else if (v.requestFullscreen) await v.requestFullscreen();
      else if (typeof v.webkitEnterFullscreen === "function") v.webkitEnterFullscreen();
    } catch (err) {
      // Fullscreen may be blocked by browser (e.g. not a user-gesture)
      // or disallowed (PIP on). Surface one-time toast so the user knows.
      console.warn("[viewer] fullscreen failed:", err);
    }
  };

  const shareToTv = async () => {
    const v = videoRef.current;
    if (!v) return;
    if (typeof v.webkitShowPlaybackTargetPicker === "function") {
      try { v.webkitShowPlaybackTargetPicker(); return; } catch { /* fall through */ }
    }
    if (v.remote?.prompt) {
      try { await v.remote.prompt(); return; } catch { /* fall through */ }
    }
    alert("Your browser's native Cast menu can mirror this to your TV. On desktop Chrome: right-click the video → Cast…");
  };

  const rotate = () => setRotation((r) => (r + 90) % 360);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    setCastSupported(
      typeof v.webkitShowPlaybackTargetPicker === "function" ||
      (!!v.remote && typeof v.remote.prompt === "function")
    );
  }, []);

  return (
    <div
      data-testid="stream-viewer"
      className={chatOpen ? "grid gap-4 grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px]" : "grid gap-4 grid-cols-1"}
    >
      {/* LEFT: Video + controls */}
      <div className="min-w-0">
        {/* VIDEO */}
        <div
          ref={containerRef}
          className="relative aspect-video rounded-2xl overflow-hidden bg-black border border-[var(--tv-border)] shadow-[0_20px_60px_-20px_rgba(0,0,0,0.9)]"
        >
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted={muted}
            x-webkit-airplay="allow"
            controlsList="nodownload"
            className="w-full h-full object-contain bg-black transition-transform duration-300"
            style={{ transform: rotation ? `rotate(${rotation}deg)` : undefined }}
            onClick={toggleMute}
          />
          {/* Brand logo watermark — bottom-right, the only on-stream mark */}
          <img
            src={kidsBrand ? "/kids-learn-play-logo.png" : "/tapintv-logo-v2.png"}
            alt=""
            aria-hidden="true"
            draggable="false"
            data-testid={`stream-viewer-watermark-${kidsBrand ? "kids" : "main"}`}
            className="pointer-events-none select-none absolute bottom-3 right-3 w-14 sm:w-16 md:w-20 opacity-85 drop-shadow-[0_0_12px_rgba(0,0,0,0.6)] z-10"
          />
          <EmoteOverlay />
          <NewsroomOverlayRender overlays={overlays} />

          {/* Guest camera PIP strip — when the host assigns a guest to CAM 1/2/3
              we surface a compact pill on the top-left of the player so viewers
              see who's "on camera". Until we have full per-guest WebRTC we use
              the guest's avatar + name as the PIP content. */}
          {(guestCams.cam1 || guestCams.cam2 || guestCams.cam3) && (
            <div
              data-testid="viewer-guest-cams"
              className="absolute top-3 left-3 z-10 flex gap-2"
            >
              {["cam1", "cam2", "cam3"].map((slot) => {
                const g = guestCams[slot];
                if (!g) return null;
                const onScreen = g.status === "onscreen";
                return (
                  <div
                    key={slot}
                    data-testid={`viewer-guest-${slot}`}
                    className={`flex items-center gap-1.5 rounded-full px-2 py-1 text-[10px] font-bold backdrop-blur ${
                      onScreen
                        ? "bg-tv-live/80 text-white border border-tv-live"
                        : "bg-black/70 text-white border border-white/15"
                    }`}
                    title={`${slot.toUpperCase()} · ${g.user_name}`}
                  >
                    {g.user_avatar ? (
                      <img
                        src={g.user_avatar}
                        alt=""
                        className="w-4 h-4 rounded-full object-cover"
                        draggable="false"
                      />
                    ) : (
                      <span className="w-4 h-4 rounded-full bg-tv-brand text-black text-[9px] font-black flex items-center justify-center">
                        {(g.user_name || "?")[0]}
                      </span>
                    )}
                    <span className="uppercase tracking-wider text-[9px] opacity-70">{slot}</span>
                    <span className="truncate max-w-[8ch]">{g.user_name}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Tap-to-play fallback when autoplay blocked */}
          {needsUserGesture && (
            <button
              data-testid="viewer-tap-to-play"
              onClick={manualPlay}
              className="absolute inset-0 z-20 flex items-center justify-center bg-black/70 backdrop-blur-sm"
            >
              <div className="flex flex-col items-center gap-3 text-white">
                <div className="w-20 h-20 rounded-full bg-[var(--tv-live)] flex items-center justify-center shadow-lg shadow-red-500/40"><Play className="w-9 h-9 fill-white" /></div>
                <div className="font-display font-black text-xl tracking-tight">Tap to play</div>
                <div className="text-white/60 text-xs">with sound on</div>
              </div>
            </button>
          )}

          {/* Muted nudge — small pill when stream is playing but muted, so the user knows to unmute */}
          {!needsUserGesture && connected && muted && (
            <button
              onClick={toggleMute}
              data-testid="viewer-unmute-nudge"
              className="absolute bottom-4 left-4 z-10 bg-[var(--tv-accent)] hover:bg-[var(--tv-accent-hover)] text-black text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-lg animate-pulse"
            >
              <VolumeX className="w-3.5 h-3.5" /> Tap to unmute
            </button>
          )}

          {/* ICE reconnection toast — centre overlay shown when the media
             * connection blips for >3s. Keeps viewers from panic-refreshing:
             * they see "Reconnecting… hang tight" instead of a frozen frame. */}
          {iceReconnecting && !connected && (
            <div
              data-testid="viewer-ice-reconnecting-toast"
              className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none"
            >
              <div className="bg-black/75 backdrop-blur-md rounded-full border border-[var(--tv-accent)]/40 px-5 py-2.5 flex items-center gap-3 shadow-[0_10px_40px_-12px_rgba(0,0,0,0.9)]">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[var(--tv-accent)] opacity-75" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[var(--tv-accent)]" />
                </span>
                <span className="text-white font-bold text-sm">Reconnecting… hang tight</span>
              </div>
            </div>
          )}

          {/* Stuck-on-waiting watchdog overlay (>25s, no broadcaster) */}
          {waitingTooLong && !connected && !broadcasterIdState && (
            <div
              data-testid="viewer-waiting-watchdog"
              className="absolute inset-0 z-30 flex items-center justify-center bg-black/80 backdrop-blur-sm p-6"
            >
              <div className="max-w-md text-center">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--tv-accent)] font-bold inline-flex items-center gap-1.5 justify-center">
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--tv-accent)] tv-pulse" />
                  Hold tight
                </div>
                <h3 className="font-display font-black text-2xl text-white mt-2">
                  {streamHealth && streamHealth.is_live === false
                    ? "Stream has ended"
                    : "Host hasn't started broadcasting yet"}
                </h3>
                <p className="text-white/70 text-sm mt-2 leading-snug">
                  {streamHealth && streamHealth.is_live === false
                    ? "The host wrapped this broadcast. Browse other live shows or check back later."
                    : "We're waiting for the host's camera signal. They may still be granting camera permissions or warming up."}
                </p>
                <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
                  <button
                    data-testid="viewer-waiting-retry"
                    onClick={() => {
                      setWaitingTooLong(false);
                      setReconnecting(false);
                      // Force a fresh WS open: explicit close triggers onclose
                      // which schedules a reconnect, then we reset the wantClose
                      // flag so it actually fires.
                      try { wsRef.current?.close(); } catch {}
                      reconnectAttemptsRef.current = 0;
                    }}
                    className="bg-[var(--tv-accent)] hover:bg-yellow-300 text-black font-display font-black text-sm px-5 py-2.5 rounded-full"
                  >
                    Try again
                  </button>
                  <button
                    data-testid="viewer-waiting-back"
                    onClick={() => window.history.back()}
                    className="bg-white/10 hover:bg-white/20 text-white font-bold text-sm px-4 py-2.5 rounded-full"
                  >
                    Back
                  </button>
                </div>
                {streamHealth && (
                  <div className="text-white/35 text-[10px] mt-4 font-mono">
                    server says: is_live={String(streamHealth.is_live)} · provider={streamHealth.provider || "?"}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Top-left status row */}
          <div className="absolute top-3 left-3 flex items-center gap-2 z-10">
            <span
              data-testid="viewer-status-badge"
              className={`text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded flex items-center gap-1.5 ${
                connected ? "bg-[var(--tv-live)]" : "bg-white/15 backdrop-blur"
              }`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-white tv-pulse" />
              {connected
                ? "LIVE"
                : iceReconnecting
                  ? "RECONNECTING…"
                  : reconnecting
                    ? "RECONNECTING…"
                    : (broadcasterIdState ? "CONNECTING…" : "WAITING FOR HOST")}
            </span>
            {viewersCount > 0 && (
              <span className="bg-black/60 backdrop-blur text-white text-xs font-semibold px-2 py-1 rounded flex items-center gap-1">
                <Users className="w-3 h-3" /> {viewersCount}
              </span>
            )}
          </div>
        </div>

        {/* CONTROL BAR — sits below the video, YouTube-style */}
        <div
          data-testid="viewer-control-bar"
          className="mt-3 flex items-center justify-between gap-2 bg-[var(--tv-elevated)] border border-[var(--tv-border)] rounded-xl px-3 py-2 backdrop-blur"
        >
          <div className="flex items-center gap-1.5">
            <ControlBtn onClick={toggleMute} testid="viewer-mute-btn" title={muted ? "Unmute" : "Mute"}>
              {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4 text-[var(--tv-accent)]" />}
            </ControlBtn>
            <ControlBtn onClick={rotate} testid="viewer-rotate-btn" title="Rotate 90°">
              <RotateCw className="w-4 h-4" />
            </ControlBtn>
            {castSupported && (
              <ControlBtn onClick={shareToTv} testid="viewer-cast-btn" title="Share to TV (AirPlay / Cast)">
                <Cast className="w-4 h-4 text-[var(--tv-brand)]" />
              </ControlBtn>
            )}
            <ControlBtn onClick={toggleFullscreen} testid="viewer-fullscreen-btn" title="Fullscreen">
              <Maximize className="w-4 h-4" />
            </ControlBtn>
          </div>

          <div className="flex items-center gap-1.5">
            {user && (
              <div data-testid="viewer-emote-picker" className="flex items-center">
                <EmotePicker onPick={sendEmote} />
              </div>
            )}
            <button
              type="button"
              onClick={() => setChatOpen((c) => !c)}
              data-testid="viewer-chat-toggle"
              className={`hidden lg:flex items-center gap-1.5 px-3 h-9 rounded-full text-xs font-bold transition ${
                chatOpen
                  ? "bg-[var(--tv-brand)] text-black hover:bg-[var(--tv-brand-hover)]"
                  : "bg-white/5 hover:bg-white/10 text-white border border-white/10"
              }`}
            >
              <MessageSquare className="w-3.5 h-3.5" /> {chatOpen ? "Hide chat" : "Show chat"}
            </button>
          </div>
        </div>
      </div>

      {/* RIGHT: Chat drawer — collapsible */}
      {chatOpen && (
        <aside
          data-testid="viewer-chat-drawer"
          className="flex flex-col rounded-2xl bg-[var(--tv-elevated)] border border-[var(--tv-border)] backdrop-blur h-[65vh] lg:h-auto lg:max-h-[calc(75vh)] overflow-hidden"
        >
          <header className="flex items-center justify-between gap-2 px-3 py-2 border-b border-[var(--tv-border)] shrink-0">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <MessageSquare className="w-4 h-4 text-[var(--tv-accent)] shrink-0" />
              <span className="font-display font-bold text-white text-sm truncate">Live chat</span>
              {viewersCount > 0 && <span className="text-white/40 text-[11px] shrink-0">· {viewersCount}</span>}
            </div>
            <button
              data-testid="viewer-chat-collapse"
              onClick={() => setChatOpen(false)}
              className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 text-white/70 hover:text-white flex items-center justify-center shrink-0 transition"
              title="Hide chat"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </header>
          <div ref={chatScrollRef} className="flex-1 overflow-y-auto px-3 py-2 space-y-1.5 text-sm">
            {chat.length === 0 && (
              <div className="flex flex-col items-center justify-center text-center text-white/40 py-8 gap-2">
                <Sparkles className="w-6 h-6 text-[var(--tv-brand)]" />
                <div className="text-xs">Say hi to the host — be the first.</div>
              </div>
            )}
            {chat.map((m) => (
              <div key={m.id} className="flex gap-2 items-start" data-testid={`viewer-chat-${m.id}`}>
                <div className="text-[var(--tv-brand)] font-semibold text-xs shrink-0 max-w-[30%] truncate">{m.name}</div>
                <div className="text-white/85 break-words">{m.text}</div>
              </div>
            ))}
          </div>
          <form onSubmit={sendChat} className="p-2 border-t border-[var(--tv-border)] flex gap-2">
            <input
              data-testid="viewer-chat-input"
              value={chatMsg}
              onChange={(e) => setChatMsg(e.target.value)}
              placeholder={user ? "Say something…" : "Sign in to chat"}
              disabled={!user}
              className="flex-1 bg-black/40 border border-[var(--tv-border)] rounded-full px-3 py-1.5 text-sm text-white outline-none focus:border-[var(--tv-brand)] disabled:opacity-60"
            />
            <button
              disabled={!user || !chatMsg.trim()}
              data-testid="viewer-chat-send"
              className="bg-[var(--tv-accent)] disabled:opacity-40 hover:bg-[var(--tv-accent-hover)] text-black text-xs font-bold px-3 rounded-full flex items-center gap-1"
            >
              <Send className="w-3 h-3" /> Send
            </button>
          </form>
        </aside>
      )}

      {/* Floating chat-open tab removed — the control-bar toggle handles open/close cleanly */}
    </div>
  );
}

function ControlBtn({ children, onClick, testid, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      title={title}
      className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 text-white/90 hover:text-white flex items-center justify-center transition"
    >
      {children}
    </button>
  );
}

function safeJson(s) { try { return JSON.parse(s); } catch { return null; } }
